import React, { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { CampingSpot } from '../../types';
import { createCustomIcon, createMixedClusterIcon, createMultiSpotIcon } from './MapUtils';
import { getSpotIcon, getSpotPosition, getSpotEventHandlers } from './markerIconCache';
import { useTranslation } from 'react-i18next';
import { Home, Globe, Navigation2 } from 'lucide-react';
import { openExternalNavigation } from '../../services/navigationService';
import { deriveRoadAccessClass, hasOffroadRoadAccess } from '../../services/roadAccessClassifier';
import L from 'leaflet';

interface ClusteredSpotMarkersProps {
  spots: CampingSpot[];
  zoom: number;
  onSelectSpot: (id: string) => void;
  selectedSpotId: string | null;
  activeAnalysisIds: Set<string>;
  isMobile: boolean;
}

const isConfirmedSpot = (spot: CampingSpot) =>
  spot.sourceLevel === 4 || spot.spotType === 'confirmed' || spot.isVerified === true;

/**
 * Premium-låst plass: statisk prikk. Bygget ÉN gang på modulnivå — lå tidligere
 * inline i `.map()`, så hver render ga et nytt L.divIcon-objekt og dermed
 * setIcon() + setLatLng() på hver låste markør (se markerIconCache.ts).
 */
const GATED_DOT_ICON = L.divIcon({
  className: 'gated-dot',
  html: `<div style="width:14px;height:14px;background:#34d399;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2);opacity:0.85;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const getClusterRadius = (zoom: number) => {
  if (zoom <= 9) return 120;
  if (zoom === 10) return 100;
  if (zoom === 11) return 85;
  if (zoom === 12) return 72;
  if (zoom === 13) return 60;
  if (zoom === 14) return 50;
  return 45;
};

const getClusterOffroadBadge = (spots: CampingSpot[]) => {
  const offroadSpots = spots.filter(hasOffroadRoadAccess);
  if (!offroadSpots.length) return { isOffroad: false, label: '4x4' };
  const allService = offroadSpots.every(s => deriveRoadAccessClass(s).roadAccessClass === 'service_road');
  return {
    isOffroad: true,
    label: allService ? 'SRV' : '4x4',
  };
};

type SpotPlaceGroup = {
  id: string;
  spots: CampingSpot[];
  location: { latitude: number; longitude: number };
};

const getSamePlaceKey = (spot: CampingSpot) =>
  `${spot.location.latitude.toFixed(4)}:${spot.location.longitude.toFixed(4)}`;

const groupSamePlaceSpots = (items: CampingSpot[]): SpotPlaceGroup[] => {
  const groups = new Map<string, CampingSpot[]>();
  for (const spot of items) {
    const key = getSamePlaceKey(spot);
    const list = groups.get(key) || [];
    list.push(spot);
    groups.set(key, list);
  }

  return Array.from(groups.entries()).map(([key, groupedSpots]) => {
    const latitude = groupedSpots.reduce((sum, spot) => sum + spot.location.latitude, 0) / groupedSpots.length;
    const longitude = groupedSpots.reduce((sum, spot) => sum + spot.location.longitude, 0) / groupedSpots.length;
    return {
      id: key,
      spots: groupedSpots,
      location: { latitude, longitude },
    };
  });
};

const ClusteredSpotMarkers: React.FC<ClusteredSpotMarkersProps> = ({
  spots, zoom, onSelectSpot, selectedSpotId, activeAnalysisIds, isMobile
}) => {
  const { t } = useTranslation();
  const maxClusterRadius = useMemo(() => getClusterRadius(zoom), [zoom]);

  const selectedSpot = useMemo(() => selectedSpotId ? spots.find(s => s.id === selectedSpotId) : null, [spots, selectedSpotId]);
  const { gatedSpots, confirmedSpots, unconfirmedSpots } = useMemo(() => {
    const gated: CampingSpot[] = [];
    const confirmed: CampingSpot[] = [];
    const unconfirmed: CampingSpot[] = [];
    for (const s of spots) {
      if (s.id === selectedSpotId) continue;
      if (s.isPremiumGated) { gated.push(s); }
      else if (isConfirmedSpot(s)) { confirmed.push(s); }
      else { unconfirmed.push(s); }
    }
    return { gatedSpots: gated, confirmedSpots: confirmed, unconfirmedSpots: unconfirmed };
  }, [spots, selectedSpotId]);

  return (
    <>
      {/* Selected spot: always rendered outside clusters */}
      {selectedSpot && !selectedSpot.isPremiumGated && (
        <Marker
          key={`selected-${selectedSpot.id}`}
          position={getSpotPosition(selectedSpot)}
          icon={getSpotIcon(selectedSpot, true, activeAnalysisIds.has(selectedSpot.id))}
          zIndexOffset={1200}
          eventHandlers={getSpotEventHandlers(selectedSpot, onSelectSpot)}
        />
      )}

      {/* Confirmed spots cluster */}
      {confirmedSpots.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          chunkInterval={120}
          chunkDelay={20}
          maxClusterRadius={maxClusterRadius}
          showCoverageOnHover={false}
          removeOutsideVisibleBounds
          spiderfyOnMaxZoom={true}
          animate={true}
          iconCreateFunction={(cluster: any) => {
	            try {
	              const childMarkers = cluster.getAllChildMarkers();
	              const spotTypes = new Set(childMarkers.map((m: any) => m.options._spotType));
	              const isMixed = spotTypes.size > 1;
	              const childSpots = childMarkers.map((m: any) => m.options._spot).filter(Boolean) as CampingSpot[];
	              const representative = childSpots[0];
	              const offroadBadge = getClusterOffroadBadge(childSpots);
	              if (isMixed) return createMixedClusterIcon(cluster.getChildCount(), offroadBadge.isOffroad, offroadBadge.label);
	              return representative
	                ? createCustomIcon(representative, false, false, cluster.getChildCount())
	                : createMixedClusterIcon(cluster.getChildCount(), offroadBadge.isOffroad, offroadBadge.label);
	            } catch {
	              return createMixedClusterIcon(cluster.getChildCount());
	            }
	          }}
        >
          {confirmedSpots.map((spot) => {
            return (
            <Marker
              key={spot.id}
              position={getSpotPosition(spot)}
              icon={getSpotIcon(spot, false, activeAnalysisIds.has(spot.id))}
              zIndexOffset={700}
              // @ts-ignore — custom data for iconCreateFunction
              _spot={spot}
              _spotType={spot.spotType}
              eventHandlers={getSpotEventHandlers(spot, onSelectSpot)}
            >
              {spot.spotType === 'commercial_campsite' && !isMobile && (
                <Popup className="custom-popup">
                  <div className="p-3 space-y-2 min-w-[200px]">
                    <div className="flex items-center gap-2 text-blue-600 font-black text-3xs uppercase tracking-widest">
                      <Home size={12} fill="currentColor" /> {t('map.commercial.campsite')}
                    </div>
                    <div className="text-stone-900 font-black text-xs leading-tight">{spot.name}</div>
                    <div className="flex flex-col gap-1.5 pt-1 border-t border-stone-100 mt-1">
                      {spot.commercialMetadata?.website && (
                        <a href={spot.commercialMetadata.website.startsWith('http') ? spot.commercialMetadata.website : `https://${spot.commercialMetadata.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-2xs font-bold text-blue-500 hover:underline">
                          <Globe size={12} /> {t('map.commercial.website')}
                        </a>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); openExternalNavigation(spot.location.latitude, spot.location.longitude); }} className="mt-2 w-full bg-blue-600 text-white py-1.5 rounded-lg text-3xs font-black uppercase flex items-center justify-center gap-2">
                        <Navigation2 size={12} className="rotate-45" /> {t('map.charger.navigate')}
                      </button>
                    </div>
                  </div>
                </Popup>
              )}
            </Marker>
          );})}
        </MarkerClusterGroup>
      )}

      {/* Unconfirmed spots cluster */}
      {unconfirmedSpots.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          chunkInterval={120}
          chunkDelay={20}
          maxClusterRadius={maxClusterRadius}
          showCoverageOnHover={false}
          removeOutsideVisibleBounds
          spiderfyOnMaxZoom={true}
          animate={true}
	          iconCreateFunction={(cluster: any) => {
	            try {
	              const childMarkers = cluster.getAllChildMarkers();
	              const spotTypes = new Set(childMarkers.map((m: any) => m.options._spotType));
	              const isMixed = spotTypes.size > 1;
	              const childSpots = childMarkers.map((m: any) => m.options._spot).filter(Boolean) as CampingSpot[];
	              const representative = childSpots[0];
	              const offroadBadge = getClusterOffroadBadge(childSpots);
	              if (isMixed) return createMixedClusterIcon(cluster.getChildCount(), offroadBadge.isOffroad, offroadBadge.label);
	              return representative
	                ? createCustomIcon(representative, false, false, cluster.getChildCount())
	                : createMixedClusterIcon(cluster.getChildCount(), offroadBadge.isOffroad, offroadBadge.label);
	            } catch {
	              return createMixedClusterIcon(cluster.getChildCount());
	            }
	          }}
        >
          {unconfirmedSpots.map((spot) => {
            return (
              <Marker
                key={spot.id}
                position={getSpotPosition(spot)}
                icon={getSpotIcon(spot, false, activeAnalysisIds.has(spot.id))}
                zIndexOffset={0}
                // @ts-ignore — custom data for iconCreateFunction
                _spot={spot}
                _spotType={spot.spotType}
                eventHandlers={getSpotEventHandlers(spot, onSelectSpot)}
              />
            );
          })}
        </MarkerClusterGroup>
      )}

      {/* Premium-gated spots: simple dots */}
      {gatedSpots.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          chunkInterval={120}
          chunkDelay={20}
          maxClusterRadius={maxClusterRadius}
          showCoverageOnHover={false}
          removeOutsideVisibleBounds
          spiderfyOnMaxZoom={true}
          animate={true}
          iconCreateFunction={(cluster: any) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              className: 'gated-cluster',
              html: `<div style="width:18px;height:18px;background:#34d399;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2);opacity:0.85;display:flex;align-items:center;justify-content:center;color:white;font-size:8px;font-weight:bold;">${count}</div>`,
              iconSize: [18, 18],
              iconAnchor: [9, 9]
            });
          }}
        >
          {gatedSpots.map((spot) => (
            <Marker
              key={`gated-${spot.id}`}
              position={getSpotPosition(spot)}
              icon={GATED_DOT_ICON}
              zIndexOffset={50}
              eventHandlers={getSpotEventHandlers(spot, onSelectSpot)}
            />
          ))}
        </MarkerClusterGroup>
      )}
    </>
  );
};

export default React.memo(ClusteredSpotMarkers);
